import React, { useEffect, useMemo, useState } from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';

// redux
import { useAppDispatch } from 'redux/store';
import { getAiRolePlayAvailableFaces } from 'redux/actions/ai-role-play';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';

// hooks
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import useUploadFile from 'shared/hooks/use-upload-file';

// styles
import { cardIconSize } from 'styles/global_defaults/scaffolding';
import { gray2, gray7 } from 'styles/global_defaults/colors';

// components
import NvFilePicker from 'shared/components/nv-filepicker';
import NvTooltip from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import { S3NameSpaces } from 'shared/services/s3-upload-factory';
import Spinner from 'shared/components/spinner';
import { sharedStyles } from '../styles';
import { AvatarItem } from './types';
import {
  AIBackToRolePlayButton,
  AICharacterIcon,
  AICharacterVoice,
  AIPersonality,
  AIProfilePhoto,
  AIRoleAndName,
  buildCustomFacesPayload,
} from './ai-character-utils';

const styles = css`
  .upload-container {
    width: ${cardIconSize}px;
    height: ${cardIconSize}px;
    border-radius: 50%;
    align-content: center;
    border: 1px dashed ${gray2};
    max-width: ${cardIconSize}px;
    min-width: ${cardIconSize}px;

    &:hover {
      background: ${gray7};
    }
  }

  .avatar-grid {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
`;

export type CharacterEditModalProps = {
  onBackToRolePlay?: () => void;
};

const AICharacterEditView = (props: CharacterEditModalProps) => {
  const { onBackToRolePlay } = props;
  const { setValue, getValues } = useFormContext();

  const dispatch = useAppDispatch();
  const params = useLecturePageParams();
  const aiCharacterFace = getValues('aiCharacterFace');
  const aiRolePlayAvailableFacePresets = useSelector((state) => state.models.aiRolePlayAvailableFacePresets);
  const { uploadFiles, isUploading } = useUploadFile();
  const [uploadedFaces, setUploadedFaces] = useState<AvatarItem[]>([]);
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(null);

  const presetFaces: AvatarItem[] = useMemo(
    () => aiRolePlayAvailableFacePresets.map((face) => ({
      id: face.presetKey,
      source: 'preset',
      displayUrl: face.displayUrl,
      name: face.name,
      presetKey: face.presetKey,
    })),
    [aiRolePlayAvailableFacePresets],
  );

  const allFaces: AvatarItem[] = [
    ...uploadedFaces,
    ...presetFaces,
  ];

  useEffect(() => {
    const modalBody = document.querySelector<HTMLElement>(
      '.ai-role-play-config-modal, .ai-role-play-edit-modal',
    );

    modalBody?.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    dispatch(getAiRolePlayAvailableFaces({
      catalogId: params.catalogId,
    }));
  }, [dispatch, params.catalogId]);

  useEffect(() => {
    if (aiCharacterFace?.presetKey === 'default' && !aiCharacterFace?.displayUrl) {
      const defaultPreset = presetFaces.find(
        (face) => face.presetKey === 'default',
      );

      if (!defaultPreset) return;
      setSelectedAvatarId(defaultPreset.id);
      setValue(
        'aiCharacterFace',
        {
          source: 'preset',
          presetKey: defaultPreset.presetKey,
          displayUrl: defaultPreset.displayUrl,
          customFaces: [],
        },
        { shouldDirty: false },
      );
    }
  }, [presetFaces, aiCharacterFace, setValue]);


  useEffect(() => {
    if (!aiCharacterFace) return;
    // restore uploaded custom faces
    const restoredCustomFaces: AvatarItem[] = (aiCharacterFace.customFaces || []).map((face) => ({
      id: face.uniqueId,
      source: 'custom',
      displayUrl: face.displayUrl,
      customFileData: {
        uniqueId: face.uniqueId,
        fileName: face.fileName,
        fileSize: face.fileSize,
        contentType: face.contentType,
        displayUrl: face.displayUrl,
      },
    }));
    setUploadedFaces(restoredCustomFaces);

    // restore selected avatar
    if (aiCharacterFace.source === 'preset') {
      setSelectedAvatarId(aiCharacterFace.presetKey);
    } else {
      const selectedFace = aiCharacterFace.customFaces?.find((face) => face.selected);
      setSelectedAvatarId(selectedFace?.uniqueId || null);
    }
  }, [aiCharacterFace]);

  const handleAvatarSelection = (avatar: AvatarItem) => {
    setSelectedAvatarId(avatar.id);
    const customFacesPayload = buildCustomFacesPayload(uploadedFaces, avatar.id);

    if (avatar.source === 'preset') {
      setValue(
        'aiCharacterFace',
        {
          source: 'preset',
          presetKey: avatar.presetKey,
          displayUrl: avatar.displayUrl,
          customFaces: customFacesPayload,
        },
        { shouldDirty: true },
      );
    } else {
      setValue(
        'aiCharacterFace',
        {
          source: 'custom',
          presetKey: null,
          displayUrl: avatar.displayUrl,
          customFaces: customFacesPayload,
        },
        { shouldDirty: true },
      );
    }
  };

  const onFileError = () => {
    dispatch(addAlertMessage({
      type: AlertMessageType.ERROR,
      header: t.FORM.OOPS(),
      message: t.FILE_UPLOAD.NOT_SUPPORTED_ALERT(),
    }));
  };

  const handleProfilePhotoUpload = async (file: File) => {
    try {
      const [novoEdFile] = await uploadFiles([file], S3NameSpaces.CONTENT_TEMPLATES);
      if (!novoEdFile?.url) {
        onFileError();
        return;
      }

      const uploadFace: AvatarItem = {
        id: novoEdFile.uniqueId,
        source: 'custom',
        displayUrl: novoEdFile.url,
        customFileData: {
          uniqueId: novoEdFile.uniqueId,
          fileName: novoEdFile.name,
          fileSize: novoEdFile.size,
          contentType: novoEdFile.type,
          displayUrl: novoEdFile.url,
        },
      };
      setSelectedAvatarId(novoEdFile.uniqueId);

      setUploadedFaces((prev) => {
        const updatedFaces = [uploadFace, ...prev];
        const customFacesPayload = buildCustomFacesPayload(updatedFaces, uploadFace.id);
        setValue(
          'aiCharacterFace',
          {
            source: 'custom',
            presetKey: null,
            displayUrl: novoEdFile.url,
            customFaces: customFacesPayload,
          },
          { shouldDirty: true },
        );
        return updatedFaces;
      });
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FILE_UPLOAD.NOT_SUPPORTED_ALERT(),
      }));
    }
  };

  const handleDeleteCustomPhoto = (e: React.MouseEvent, avatarId: string) => {
    e.stopPropagation();
    const updatedFaces = uploadedFaces.filter(
      (face) => face.id !== avatarId,
    );
    setUploadedFaces(updatedFaces);
    const customFacesPayload = buildCustomFacesPayload(updatedFaces, selectedAvatarId);

    setValue(
      'aiCharacterFace',
      {
        ...getValues('aiCharacterFace'),
        customFaces: customFacesPayload,
      },
      { shouldDirty: true },
    );
  };

  return (
    <div css={[styles, sharedStyles]}>
      <AIBackToRolePlayButton
        onBackToRolePlay={onBackToRolePlay}
      />
      <AICharacterIcon />
      <AIRoleAndName />
      <AIPersonality />

      {/* AI Profile Photo */}
      <div>
        <div className='text-large-body lh-25 font-weight-bolder'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_PROFILE_PHOTO()}
        </div>
        <div className='text-large-body lh-25 mt-1 mb-2'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_PROFILE_PHOTO_UPLOAD_TEXT()}
        </div>

        <div className='avatar-grid'>
          {isUploading ? (
            <div
              className='upload-container'
              aria-label={t.FORM.SAVING()}
            >
              <div className='text-align-center'>
                <Spinner size={16} />
              </div>
            </div>
          ) : (
            <NvTooltip
              text={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_PROFILE.SELECT_CUSTOM_IMAGE()}
            >
              <NvFilePicker
                multiple={false}
                accept={['image/*']}
                onChange={([file]) => handleProfilePhotoUpload(file)}
                onSelectError={onFileError}
                className='upload-container'
              >
                <div className='text-align-center'>
                  <NvIcon icon='upload' size='small' className=' color-gray-2' />
                  <div className='bold color-gray-2'>
                    {t.ORG_LEVEL_PROFILE.FIELD_MODIFIER.UPLOAD()}
                  </div>
                </div>
              </NvFilePicker>
            </NvTooltip>
          )}

          {allFaces.map((avatar) => (
            <AIProfilePhoto
              key={avatar.id}
              avatar={avatar}
              isSelected={selectedAvatarId === avatar.id}
              isCustom={avatar.source === 'custom'}
              onSelect={handleAvatarSelection}
              onDelete={handleDeleteCustomPhoto}
            />
          ))}
        </div>
      </div>

      <AICharacterVoice />
    </div>
  );
};

export default AICharacterEditView;
